import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, AlertCircle } from 'lucide-react';
import ConsistencyLogo from '@/components/ConsistencyLogo';

const API_URL = window.__BACKEND_URL;

export default function LegalDocumentViewer() {
  const { slug } = useParams();
  const [docData, setDocData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/public/legal/vault/${slug}`);
        
        if (res.ok) {
          const data = await res.json();
          setDocData(data);
        } else if (res.status === 404) {
          setError('Document not found');
        } else {
          setError('Failed to load document');
        }
      } catch (err) {
        console.error('Error fetching document:', err);
        setError('Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchDocument();
    }
  }, [slug]);

  // Disable right-click context menu
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    window.document.addEventListener('contextmenu', handleContextMenu);
    return () => window.document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  // Disable keyboard shortcuts for print/save
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 's' || e.key === 'P' || e.key === 'S')) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0b0d] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error || !docData) {
    return (
      <div className="min-h-screen bg-[#0a0b0d] flex flex-col items-center justify-center text-white">
        <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="text-2xl font-bold mb-2">Document Unavailable</h1>
        <p className="text-gray-400 mb-6">This document is not available or may have been removed.</p>
        <a 
          href="/" 
          className="px-4 py-2 bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors"
        >
          Go to Home
        </a>
      </div>
    );
  }

  // Format text content with proper paragraphs
  const formatContent = (text) => {
    if (!text) return null;
    
    // Split by double newlines for paragraphs
    const paragraphs = text.split(/\n\n+/);
    
    return paragraphs.map((para, idx) => {
      // Check if it looks like a heading (short, possibly all caps or ends with colon)
      const isHeading = para.length < 100 && (
        para === para.toUpperCase() || 
        para.endsWith(':') ||
        /^[A-Z0-9\s.]+$/.test(para.trim())
      );
      
      if (isHeading) {
        return (
          <h2 key={idx} className="text-xl font-semibold text-white mt-6 mb-3">
            {para.trim()}
          </h2>
        );
      }
      
      // Regular paragraph - preserve single line breaks
      const lines = para.split('\n');
      return (
        <p key={idx} className="text-gray-300 leading-relaxed mb-4">
          {lines.map((line, lineIdx) => (
            <React.Fragment key={lineIdx}>
              {line}
              {lineIdx < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      );
    });
  };

  return (
    <div 
      className="min-h-screen bg-[#0a0b0d] flex flex-col select-none"
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      {/* Header */}
      <header className="bg-[#121417] border-b border-gray-800 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <ConsistencyLogo variant="symbol" size="sm" />
            <span className="text-white font-semibold">Consistency</span>
          </a>
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-cyan-500" />
            <span className="text-white font-medium">{docData?.title || 'Legal Document'}</span>
          </div>
        </div>
      </header>

      {/* Document Content */}
      <main className="flex-1 px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <h1 className="text-3xl font-bold text-white mb-2">
            {docData.title}
          </h1>
          
          {/* Last updated */}
          {docData.updated_at && (
            <p className="text-sm text-gray-500 mb-8">
              Last updated: {new Date(docData.updated_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          )}
          
          {/* Content */}
          <div className="bg-[#121417] rounded-lg border border-gray-800 p-8">
            {docData.content_text ? (
              <div className="prose prose-invert max-w-none">
                {formatContent(docData.content_text)}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">
                Document content is being processed. Please check back shortly.
              </p>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#121417] border-t border-gray-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between text-sm text-gray-500">
          <span>© {new Date().getFullYear()} Consistency. All rights reserved.</span>
          <span>Read-only document</span>
        </div>
      </footer>
    </div>
  );
}
